import { useCallback, useLayoutEffect, useState } from 'react';

import { UseMutateAsyncFunction } from '@tanstack/react-query';
import {
  ColumnSizingState,
  VisibilityState,
  ColumnOrderState,
  SortingState,
  RowSelectionState,
} from '@tanstack/react-table';
import produce from 'immer';
import create from 'zustand';

import { useDeepEqualMemoValue } from '@components/component-helpers';
import { useEvent } from '@hooks/use-event';
import { ImmerUpdateFn, Timeout } from '@utils/types';

import { FilterData, AppliedFilterData, FilterStoreState } from './filters/common';
import { AvailableColumn, DataItemType, TableView } from './types';
import { defaultSizingMap } from './use-column-sizing-vars';

// Only update settings every 1 second max, just to prevent needless
// spam to the API if the user is rapidly messing with column settings.
const PERSIST_TIMEOUT = 1000;

// React-table expects react-like setState functions for their state
// so the handler needs to handle either a value or an updater fn.
type ReactTableSafeUpdater<T> = T | ((old: T) => T);

type TableStoreState = {
  // Persisted state
  columnSizing: ColumnSizingState;
  columnVisibility: VisibilityState;
  columnOrder: ColumnOrderState;
  sortingState: SortingState;
  filters: FilterStoreState;

  // Local state that doesnt need to be persisted as its more for
  // UX while the user has the table loaded and is working with it.
  userResized: Set<string>;
  selection: RowSelectionState;

  // Column IDs currently provided by useColumnSync. Used by the
  // views manager to scope dirty-checks so that system-driven
  // column changes (add/remove/disable attribute) don't mark the
  // view as having unsaved changes.
  _availableColumnIds: string[];
};

type TableStoreConfig = {
  selectedView?: TableView;
  onPersist?(state: TableView): void;
  externalFilters?: boolean;
};

// Enable cancelling and re-queueing a new timeout to persist
let persistTimeout: Timeout;

export const createTableStore = ({
  selectedView,
  onPersist,
  externalFilters = false,
}: TableStoreConfig) => {
  let storedPersist = onPersist;

  const useStore = create<TableStoreState>(() => {
    return {
      columnOrder: selectedView?.columnOrder ?? [],
      userResized: new Set(Object.keys(selectedView?.columnSizing || {})),
      columnSizing: selectedView?.columnSizing ?? {},
      columnVisibility: selectedView?.columnVisibility ?? {},
      sortingState: selectedView?.sortingState ?? [],
      selection: {},
      filters: {
        appliedFilters: selectedView?.appliedFilters ?? [],
        conjunction: selectedView?.conjunction ?? 'and',
        isOpen: false,
      },
      _availableColumnIds: [],
    };
  });

  const doPersist = () => {
    storedPersist?.(getViewState(useStore.getState()));
  };

  const persist = () => {
    clearTimeout(persistTimeout);
    if (externalFilters) {
      doPersist();
      return;
    }
    persistTimeout = setTimeout(() => {
      doPersist();
    }, PERSIST_TIMEOUT);
  };

  const getViewState = (fromState?: TableStoreState): TableView => {
    const state = fromState ?? useStore.getState();
    let newSizing = state.columnSizing;

    if (state.userResized.size > 0) {
      let sizing: ColumnSizingState = {};
      // We only store the sizes of the columns that were manually changed,
      // so the rest of the columns can flow naturally by screen size.
      Object.keys(newSizing).forEach((columnId) => {
        if (state.userResized.has(columnId)) {
          sizing[columnId] = newSizing[columnId];
        }
      });
      newSizing = sizing;
    }

    return {
      id: selectedView?.id ?? '',
      appliedFilters: state.filters.appliedFilters,
      columnOrder: state.columnOrder,
      columnSizing: newSizing,
      columnVisibility: state.columnVisibility,
      conjunction: state.filters.conjunction,
      sortingState: state.sortingState,
    };
  };

  const update = (fn: ImmerUpdateFn<TableStoreState>) => {
    return useStore.setState((state) => produce(state, fn));
  };

  const updateTableView = (fn: ImmerUpdateFn<TableStoreState>) => {
    update(fn);
    persist();
  };

  // Query inputs (filters, conjunction, sorting). In external-filter mode the
  // rendered rows come from a backend query keyed on the persisted view and
  // there is no "Save view" step, so every change must reach onPersist or it
  // has no effect (the chip disappears / the sort arrow flips, but the data
  // never re-queries — external mode also disables client-side sorting). In
  // internal mode we only touch local state: filtering/sorting happen
  // client-side off the live store, and the views manager owns when changes
  // get saved, so auto-persisting here would let it revert the change.
  const updateAndPersistExternal = (fn: ImmerUpdateFn<TableStoreState>) => {
    update(fn);
    if (externalFilters) {
      persist();
    }
  };

  const clearFilters = () => {
    updateAndPersistExternal((state) => {
      state.filters.appliedFilters = [];
      state.filters.conjunction = 'and';
      state.filters.isOpen = false;
    });
  };

  const addFilter = (newFilter: FilterData) => {
    updateAndPersistExternal((state) => {
      state.filters.appliedFilters.push(newFilter as AppliedFilterData);
    });
  };

  const removeFilter = (id: string) => {
    updateAndPersistExternal((state) => {
      state.filters.appliedFilters = state.filters.appliedFilters.filter(
        (filter) => filter.id !== id
      );
      if (state.filters.appliedFilters.length === 0) {
        state.filters.isOpen = false;
      }
    });
  };

  const updateFilter = (updatedFilter: AppliedFilterData) => {
    updateAndPersistExternal((state) => {
      state.filters.appliedFilters = state.filters.appliedFilters.map((filter) => {
        if (filter.id === updatedFilter.id) {
          return updatedFilter;
        }
        return filter;
      });
    });
  };

  const toggleFilters = () => {
    updateTableView((state) => {
      state.filters.isOpen = !state.filters.isOpen;
    });
  };

  const collapseFilters = () => {
    updateTableView((state) => {
      state.filters.isOpen = false;
    });
  };

  const expandFilters = () => {
    updateTableView((state) => {
      state.filters.isOpen = true;
    });
  };

  const switchConjunction = () => {
    updateAndPersistExternal((state) => {
      state.filters.conjunction = state.filters.conjunction === 'and' ? 'or' : 'and';
    });
  };

  const filterActions = {
    clearFilters,
    addFilter,
    updateFilter,
    removeFilter,
    toggleFilters,
    collapseFilters,
    expandFilters,
    switchConjunction,
  };

  const useFilters = () => {
    const filters = useStore((state) => state.filters);
    return {
      filters,
      actions: filterActions,
    };
  };

  const setSortingState = (sorting: ReactTableSafeUpdater<SortingState>) => {
    updateAndPersistExternal((state) => {
      state.sortingState = typeof sorting === 'function' ? sorting(state.sortingState) : sorting;
    });
  };

  const useSortingState = () => {
    const sortingState = useStore((state) => state.sortingState);
    return { sortingState, setSortingState };
  };

  const setColumnSizing = (sizing: ReactTableSafeUpdater<ColumnSizingState>) => {
    updateTableView((state) => {
      state.columnSizing = typeof sizing === 'function' ? sizing(state.columnSizing) : sizing;
    });
  };

  const setColumnVisibility = (visibility: ReactTableSafeUpdater<VisibilityState>) => {
    updateTableView((state) => {
      state.columnVisibility =
        typeof visibility === 'function' ? visibility(state.columnVisibility) : visibility;
    });
  };

  const setColumnOrder = (order: ReactTableSafeUpdater<ColumnOrderState>) => {
    updateTableView((state) => {
      state.columnOrder = typeof order === 'function' ? order(state.columnOrder) : order;
    });
  };

  const setUserResized = (resized: Set<string>) => {
    updateTableView((state) => {
      state.userResized = resized;
    });
  };

  const useColumnSizing = () => {
    const columnSizing = useStore((state) => state.columnSizing);
    const userResized = useStore((state) => state.userResized);

    return {
      columnSizing,
      userResized,
      setColumnSizing,
      setUserResized,
    };
  };

  const useColumnOrder = () => {
    const columnOrder = useStore((state) => state.columnOrder);

    return {
      columnOrder,
      setColumnOrder,
    };
  };

  const useColumnVisibility = () => {
    const columnVisibility = useStore((state) => state.columnVisibility);
    return { columnVisibility, setColumnVisibility };
  };

  const addColumn = (id: string) => {
    update((state) => {
      if (!state.columnOrder.includes(id)) {
        state.columnOrder.push(id);
      }

      state.columnVisibility[id] = true;
    });
  };

  const removeColumn = (id: string) => {
    update((state) => {
      state.columnOrder = state.columnOrder.filter((columnId) => columnId !== id);
      delete state.columnVisibility[id];
    });
  };

  const useTableSelection = <DataType extends DataItemType>({
    data,
    onSelectChange,
    idColumn = 'id' as keyof DataType,
  }: {
    data: Array<DataType>;
    onSelectChange?: (selectedData: Array<DataType>) => void;
    idColumn?: keyof DataType;
  }) => {
    const selectionState = useStore((state) => state.selection);

    const setSelection = useCallback(
      (selection: ReactTableSafeUpdater<RowSelectionState>) => {
        const newSelection =
          typeof selection === 'function' ? selection(useStore.getState().selection) : selection;
        update((state) => {
          state.selection = newSelection;
        });

        if (onSelectChange) {
          onSelectChange(data.filter((row) => newSelection[row[idColumn]]));
        }
      },
      [data, idColumn, onSelectChange]
    );

    return { selectionState, setSelection };
  };

  const useColumnSync = <DataType extends DataItemType>(
    availableColumns: Array<AvailableColumn<DataType>>
  ) => {
    useLayoutEffect(() => {
      update((state) => {
        state._availableColumnIds = availableColumns.map((c) => c.id as string);

        availableColumns.forEach((column) => {
          if (!state.columnOrder.includes(column.id)) {
            state.columnOrder.push(column.id);
          }

          if (state.columnVisibility[column.id] === undefined) {
            state.columnVisibility[column.id] = !column.defaultHidden;
          }

          if (state.columnSizing[column.id] === undefined) {
            state.columnSizing[column.id] = defaultSizingMap[column.type || 'text'];
          }
        });
      });
    }, [availableColumns]);
  };

  const resetFilters = (savedView: TableView) => {
    update((state) => {
      state.sortingState = savedView.sortingState ?? [];
      const appliedFilters = savedView.appliedFilters ?? [];
      state.filters = {
        ...state.filters,
        appliedFilters,
        conjunction: savedView.conjunction ?? 'and',
        isOpen: appliedFilters.length === 0 ? false : state.filters.isOpen,
      };
      state.columnVisibility = savedView.columnVisibility ?? state.columnVisibility;
      state.columnOrder = savedView.columnOrder ?? [];
    });
  };

  const syncConfig = ({ onPersist, selectedView }: TableStoreConfig) => {
    storedPersist = onPersist;

    if (!selectedView) return;

    update((state) => {
      state.columnOrder = selectedView?.columnOrder ?? [];
      state.columnSizing = {
        ...state.columnSizing,
        ...selectedView?.columnSizing,
      };
      state.userResized = new Set(Object.keys(selectedView?.columnSizing || {}));
      state.columnVisibility = selectedView?.columnVisibility ?? {};
      state.sortingState = selectedView?.sortingState ?? [];
      state.filters = {
        ...state.filters,
        appliedFilters: selectedView.appliedFilters ?? [],
        conjunction: selectedView.conjunction ?? 'and',
      };
    });
  };

  return {
    isExternalMode: Boolean(externalFilters),
    actions: {
      getState: useStore.getState,
      persist,
      persistImmediate: doPersist,
      getViewState,
      addColumn,
      removeColumn,
      resetFilters,
      setSortingState,
      syncConfig,
    },
    // Same object handed out by useFilters(), exposed here for imperative use
    // outside React (and so the persist behaviour can be unit-tested).
    filterActions,
    useStore,
    useColumnSync,
    useColumnOrder,
    useColumnSizing,
    useColumnVisibility,
    useFilters,
    useSortingState,
    useTableSelection,
  } as const;
};

type UsePersistedTableStoreProps = {
  tableView: TableView;
  setTableView: UseMutateAsyncFunction<any, unknown, TableView, unknown>;
};

export const usePersistedTableStore = ({
  tableView,
  setTableView,
}: UsePersistedTableStoreProps) => {
  const onPersist = useEvent((newState: TableView) => {
    setTableView(newState);
  });

  const tableStore = useTableStore({
    selectedView: tableView,
    onPersist,
  });

  return tableStore;
};

export const useTableStore = (config: TableStoreConfig) => {
  const memoConfig = useDeepEqualMemoValue(config);
  const store = useState(() => createTableStore(memoConfig))[0];

  useLayoutEffect(() => {
    store.actions.syncConfig(memoConfig);
  }, [memoConfig, store]);

  return store;
};

export type TableStore = ReturnType<typeof useTableStore>;

export type FilterActions = {
  clearFilters: () => void;
  addFilter: (newFilter: FilterData) => void;
  updateFilter: (updatedFilter: AppliedFilterData) => void;
  removeFilter: (id: string) => void;
  toggleFilters: () => void;
  collapseFilters: () => void;
  expandFilters: () => void;
  switchConjunction: () => void;
};
